// A visible mark on output that has not been validated against reality (#255).
//
// WHY A CHIP AND NOT MORE PROSE.
//
// The DLR panel's copy is already careful — the title ends "(indicative)" and
// the subtitle ends "and is not a rating to dispatch against". But prose is
// read once and then skipped, and it does not survive a SCREENSHOT. These
// panels get photographed into slides, status emails and chat messages, and the
// caveat has to travel with the number. A chip beside the heading does; a
// subtitle gets cropped.
//
// WHY "BETA" WHEN THE PROBLEM IS NOT SOFTWARE MATURITY.
//
// "Beta" reads as unfinished UI and expected bugs. What is actually true of the
// line rating is narrower and more serious: no conductor temperature is
// measured anywhere in this system, so no rating this panel has produced has
// ever been checked against the thing it predicts. The word is on the chip
// because every operator understands it at a glance; the specific limitation is
// in the tooltip, which is where someone who stops to ask will look.
//
// WHY IT IS SHARED.
//
// Two other surfaces are in the same position and should carry the same mark:
// the ML anomaly tier, silent at `medium` and never reviewed against a
// confirmed event, and the degradation-trend flag, which DegradationTrend.jsx
// currently handles by hand with an uncoloured `dtFlagUncalibrated` line. That
// is the right instinct as a one-off; this makes it a convention.
//
// Removing it is deleting one line at the call site, which is deliberate: the
// day a measured conductor temperature exists, this must be cheap to take off
// rather than furniture nobody dares touch.
import React from "react";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

/**
 * @param label   short word on the chip. Defaults to the translated "Beta".
 * @param title   the actual limitation, shown on hover and as the a11y label.
 * @param t       translator from the language context.
 */
export default function BetaBadge({ t = (k) => k, label, title }) {
  const text = label ?? t("betaBadge");
  const explanation = title ?? t("betaBadgeWhy");
  return (
    <Tooltip title={explanation}>
      <Chip
        label={text}
        size="small"
        // Outlined and uncoloured on purpose. A filled warning-coloured chip
        // reads as "something is wrong right now", which is not the claim —
        // the claim is "this has not been checked". Same reasoning as the
        // deliberately uncoloured degradation flag.
        variant="outlined"
        // The explanation already opens with the word on the chip, so it is the
        // accessible name on its own. Concatenating the two said "Beta: Beta:
        // indicative only ...", which a screen reader reads aloud twice.
        aria-label={explanation}
        // Without `clickable` or `onDelete`, MUI renders the chip as a bare
        // <div> with no role, and an aria-label on a role-less element is not
        // reliably announced. `note` is the role for an aside about the content
        // beside it, which is exactly what this is.
        role="note"
        // And it has to be reachable without a mouse: the tooltip carries the
        // real limitation, and `focus` is what opens it for a keyboard user.
        tabIndex={0}
        sx={{
          height: 20,
          fontSize: 11,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          borderColor: "text.disabled",
          color: "text.secondary",
          // The chip sits beside a long heading. Shrinking it would ellipsise
          // "BETA" to "BE...", which defeats the one thing it is here to do:
          // survive a screenshot of the panel.
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
}
